import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { withStyles } from '@material-ui/core'

import Modal from '../../../uiKit/Modal'
import Select from '../../../uiKit/Select/Select'
import {
  DEFAULT_QCS_LIMITS,
  USAGE_METRIC,
  USAGE_METRICS,
  USAGE_METRIC_META,
  isUnlimited,
} from '../../../constants/usageLimits'
import {
  SUPERVISION_MODE,
  SUPERVISION_MODE_LABEL,
  SUPERVISION_MODE_OPTIONS,
} from '../../../constants/supervisionMode'
import { alertError, alertSuccess } from '../../../api'
import { updateBotPackageConfig, assignOrChangeBotPackage, getBotPackage } from '../api/botPackage'
import {
  supervisionModeToConfig,
  isBotPackageCarePlanEnabled,
  isBotPackageSupervisionEnabled,
} from '../api/usageLimits'
import {
  fetchPackages,
  enabledCount,
  PACKAGE_STATUS,
  getCachedUsageLimitRange,
} from '../helpers/packageTiers'
import {
  getOrgPackageId,
  setOrgPackageId,
  cacheBotPackageAssignment,
  getCachedBotPackageAssignment,
} from '../helpers/orgPackages'
import TierSelect from './TierSelect'

// Supervision has its own dropdown (see SUPERVISION_MODE_OPTIONS below), not a
// numeric limit — everything else in USAGE_METRICS still gets the stepper.
const LIMIT_METRICS = USAGE_METRICS.filter(metric => metric !== USAGE_METRIC.SUPERVISION_SESSIONS)

const SUPERVISION_MODE_SELECT_OPTIONS = SUPERVISION_MODE_OPTIONS.map(mode => ({
  value: mode,
  label: SUPERVISION_MODE_LABEL[mode],
}))

// In-flow menu: it takes up space below the field and pushes the sticky footer
// down instead of floating over it. Replaces the shared Select's `menu` style,
// so its radius / shadow / z-index are repeated here.
const SUPERVISION_MODE_SELECT_STYLES = {
  menu: provided => ({
    ...provided,
    position: 'relative',
    borderRadius: 10,
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
    zIndex: 10000,
  }),
}

const getScrollParent = node => {
  let el = node && node.parentElement
  while (el) {
    const { overflowY } = window.getComputedStyle(el)
    if (overflowY === 'auto' || overflowY === 'scroll') return el
    el = el.parentElement
  }
  return null
}

/** Prefer list-embedded packageDetails, then the assignment cache. */
const assignmentForOrganisation = organisation =>
  organisation?.bot?.packageDetails ||
  getCachedBotPackageAssignment(organisation?.id) ||
  null

/**
 * Sets one organisation's monthly allowances.
 *
 * This is a CareBrain operation, so it lives on the QCS home page — where an
 * operator is already looking across organisations — rather than inside the
 * customer's own settings, which shows Supervision and nothing else.
 *
 * Each metric can be a number or unlimited. Unlimited is an explicit checkbox
 * rather than "leave it blank", because a blank field reads as "not set yet"
 * and an operator should never have to guess which one they meant.
 */

const styles = () => ({
  dialogPaper: {
    width: 600,
    maxWidth: '100%',
    // No bottom padding — the sticky footer below supplies the bottom spacing so
    // it can sit flush with the scroll area's bottom edge (no gap for scrolled
    // content to show through).
    padding: '28px 30px 0',
  },
  body: {
    fontFamily: 'var(--careco-font)',
  },
  // Header — left aligned, close ✕ stays top-right (drawn by the Modal shell).
  header: {
    paddingRight: 28,
    marginBottom: 'var(--careco-space-xl)',
  },
  title: {
    margin: 0,
    fontSize: 20,
    fontWeight: 700,
    lineHeight: 1.25,
    color: 'var(--careco-text-primary)',
  },
  intro: {
    margin: '10px 0 0',
    fontSize: 14,
    lineHeight: '21px',
    color: 'var(--careco-text-secondary)',
  },

  // Each metric is an independent section, separated by a subtle divider with
  // generous breathing room so nothing crowds the controls.
  section: {
    display: 'flex',
    flexDirection: 'column',
    padding: '22px 0',
    borderTop: '1px solid var(--careco-border)',
    '&:first-of-type': { borderTop: 'none', paddingTop: 4 },
  },
  sectionHead: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  iconChip: {
    flexShrink: 0,
    width: 40,
    height: 40,
    marginRight: 14,
    borderRadius: 'var(--careco-rounded-md, 12px)',
    background: 'var(--careco-primary-light)',
    color: 'var(--careco-primary)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headText: {
    minWidth: 0,
  },
  heading: {
    margin: 0,
    fontSize: 15,
    fontWeight: 700,
    lineHeight: 1.3,
    color: 'var(--careco-text-primary)',
  },
  desc: {
    margin: '4px 0 0',
    fontSize: 13,
    lineHeight: '18px',
    color: 'var(--careco-text-muted)',
  },
  usedNow: {
    display: 'inline-block',
    marginTop: 4,
    fontSize: 12.5,
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
    color: 'var(--careco-text-muted)',
  },
  // Colour-coded against the current limit: green with headroom, amber near the
  // limit, red once it is reached — the same language as the usage bars.
  usedOk: { color: 'var(--careco-success)' },
  usedWarn: { color: 'var(--careco-status-inactive)' },
  usedSpent: { color: 'var(--careco-error)' },
  controls: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginTop: 16,
    marginLeft: 54,
  },

  // Stepper: [ − value + ] as one bordered field, buttons separated from the
  // value by hairlines.
  stepper: {
    display: 'inline-flex',
    alignItems: 'stretch',
    height: 44,
    border: '1px solid var(--careco-border)',
    borderRadius: 'var(--careco-rounded-sm, 8px)',
    background: 'var(--careco-surface)',
    overflow: 'hidden',
  },
  stepperDisabled: {
    background: 'var(--careco-row-default)',
    borderColor: 'var(--careco-border)',
  },
  stepBtn: {
    width: 42,
    border: 'none',
    background: 'transparent',
    color: 'var(--careco-primary)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'background 120ms ease',
    '&:hover': { background: 'var(--careco-row-default)' },
    '&:disabled': { color: 'var(--careco-text-muted)', cursor: 'default', background: 'transparent' },
  },
  stepInput: {
    width: 58,
    border: 'none',
    borderLeft: '1px solid var(--careco-border)',
    borderRight: '1px solid var(--careco-border)',
    textAlign: 'center',
    fontFamily: 'inherit',
    fontSize: 15,
    fontWeight: 600,
    color: 'var(--careco-text-primary)',
    background: 'transparent',
    outline: 'none',
    '&:disabled': { color: 'var(--careco-text-muted)' },
  },

  // Unlimited — a separate control beside the stepper. Explicit margin (not flex
  // `gap`, which MUI v1's JSS drops) keeps a clear 8px from the stepper.
  unlimited: {
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: 8,
    fontSize: 14,
    color: 'var(--careco-text-secondary)',
    cursor: 'pointer',
    userSelect: 'none',
  },
  hiddenInput: {
    position: 'absolute',
    opacity: 0,
    width: 1,
    height: 1,
    margin: 0,
    pointerEvents: 'none',
  },
  box: {
    flexShrink: 0,
    marginRight: 8,
    width: 18,
    height: 18,
    boxSizing: 'border-box',
    border: '1.5px solid var(--careco-border)',
    borderRadius: 5,
    background: 'var(--careco-surface)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-text-on-primary, #fff)',
    transition: 'background 120ms ease, border-color 120ms ease',
  },
  boxChecked: {
    background: 'var(--careco-primary)',
    borderColor: 'var(--careco-primary)',
  },
  belowUsage: {
    marginTop: 12,
    marginLeft: 54,
    fontSize: 12.5,
    lineHeight: '17px',
    color: 'var(--careco-status-inactive)',
  },

  // Supervision's mode picker — same field height and border as the stepper,
  // so the two sections read as one family despite one being a number and the
  // other a choice.
  select: {
    width: 220,
  },

  // Footer — a sticky action bar that stays pinned to the bottom of the
  // scrolling dialog. The negative side/bottom margins span the paper's own
  // padding (28px 30px 22px) so scrolled content never shows beneath the
  // buttons; the padding puts the spacing back inside.
  actions: {
    position: 'sticky',
    bottom: 0,
    zIndex: 1,
    display: 'flex',
    justifyContent: 'flex-end',
    // Span the paper's side padding (30px) and sit flush at the bottom (the
    // paper now has no bottom padding); the 22px bottom padding here is the
    // footer's own breathing room.
    margin: '8px -30px 0',
    padding: '16px 30px 22px',
    borderTop: '1px solid var(--careco-border)',
    background: 'var(--careco-surface)',
  },
  primary: {
    // Explicit margin, not the row's flex `gap` (dropped by MUI v1's JSS).
    marginLeft: 12,
    height: 44,
    border: 'none',
    borderRadius: 'var(--careco-rounded-sm, 8px)',
    padding: '0 24px',
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--color-text-on-primary, #fff)',
    background: 'var(--careco-primary)',
    cursor: 'pointer',
    '&:hover': { background: 'var(--careco-primary-hover)' },
    '&:disabled': { opacity: 0.5, cursor: 'default' },
  },
  ghost: {
    height: 44,
    border: '1px solid var(--careco-border)',
    background: 'transparent',
    borderRadius: 'var(--careco-rounded-sm, 8px)',
    padding: '0 22px',
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--careco-text-primary)',
    cursor: 'pointer',
    '&:hover': { background: 'var(--careco-row-default)' },
  },
})

const toFormState = usage =>
  LIMIT_METRICS.reduce((acc, metric) => {
    const entry = usage?.metrics?.[metric]
    const limit = entry ? entry.limit : DEFAULT_QCS_LIMITS[metric]

    acc[metric] = {
      unlimited: isUnlimited(limit),
      value: isUnlimited(limit) ? '' : String(limit),
      used: entry && typeof entry.used === 'number' ? entry.used : null,
    }
    return acc
  }, {})

// Lucide glyphs, drawn inline (the repo ships no lucide package).
const Glyph = ({ paths, size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    {paths.map((d, i) => (
      <path
        key={i}
        d={d}
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ))}
  </svg>
)

Glyph.propTypes = { paths: PropTypes.array, size: PropTypes.number }

const ICON = {
  // lucide `clipboard-check`
  audits: [
    'M9 4h6a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z',
    'M16 5h1a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h1',
    'M9 14l2 2 4-4',
  ],
  // lucide `users`
  sessions: [
    'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2',
    'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z',
    'M22 21v-2a4 4 0 0 0-3-3.87',
    'M16 3.13a4 4 0 0 1 0 7.75',
  ],
  // lucide `box`
  pkg: [
    'M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z',
    'm3.3 7 8.7 5 8.7-5',
    'M12 22V12',
  ],
  minus: ['M5 12h14'],
  plus: ['M12 5v14', 'M5 12h14'],
  check: ['M20 6 9 17l-5-5'],
}

const ManageLimitsModal = ({ classes, open, onClose, organisation, usage, onSaved }) => {
  const [form, setForm] = useState(() => toFormState(usage))
  const [supervisionMode, setSupervisionMode] = useState(
    organisation?.supervisionMode || SUPERVISION_MODE.DEFAULT,
  )
  const initialForm = useMemo(() => toFormState(usage), [usage])
  const initialSupervisionMode = organisation?.supervisionMode || SUPERVISION_MODE.DEFAULT
  const supervisionSelectRef = useRef(null)
  const supervisionMenuOpenRef = useRef(false)
  const supervisionMenuSettlingRef = useRef(false)

  // Supervision is the last section, so scrolling the dialog to the bottom puts
  // the field and its open menu just above the sticky footer.
  const revealSupervisionMenu = () => {
    supervisionMenuOpenRef.current = true
    supervisionMenuSettlingRef.current = true
    window.requestAnimationFrame(() => {
      const scroller = getScrollParent(supervisionSelectRef.current)
      if (scroller) scroller.scrollTop = scroller.scrollHeight
      // Scroll events caused by opening (focus, the reveal above, the browser
      // re-pinning the bottom as the menu grows) fire before the next frame's callbacks.
      window.requestAnimationFrame(() => {
        supervisionMenuSettlingRef.current = false
      })
    })
  }

  const handleSupervisionMenuClose = () => {
    supervisionMenuOpenRef.current = false
    supervisionMenuSettlingRef.current = false
  }

  // Once open, the dialog sits at its bottom, so only scrolling away from the
  // bottom comes from the user.
  const shouldCloseSupervisionMenuOnScroll = event => {
    const wrapper = supervisionSelectRef.current
    if (!supervisionMenuOpenRef.current || !wrapper) return false
    const { target } = event
    if (target instanceof Node && wrapper.contains(target)) return false
    if (target instanceof Element && target.contains(wrapper)) {
      if (supervisionMenuSettlingRef.current) return false
      return target.scrollHeight - target.clientHeight - target.scrollTop >= 1
    }
    return true
  }
  const [packageId, setPackageId] = useState(() => getOrgPackageId(organisation?.id))
  const [assignment, setAssignment] = useState(() => assignmentForOrganisation(organisation))
  const [saving, setSaving] = useState(false)
  // Live packages available to assign — refreshed when the dialog opens so a
  // package created in the builder shows up without a full page reload.
  const [hasLivePackages, setHasLivePackages] = useState(false)

  // Reopening on a different organisation must not carry the previous one's
  // numbers over — that is how an operator saves the wrong limit to the wrong
  // customer without noticing.
  useEffect(() => {
    setForm(toFormState(usage))
    setSupervisionMode(organisation?.supervisionMode || SUPERVISION_MODE.DEFAULT)
    setPackageId(getOrgPackageId(organisation?.id))
    setAssignment(assignmentForOrganisation(organisation))
  }, [organisation?.id, organisation?.supervisionMode, organisation?.bot, usage])

  useEffect(() => {
    if (!open || !organisation?.id) return undefined
    let cancelled = false
    getBotPackage(organisation.id)
      .then(next => {
        if (cancelled) return
        if (next) {
          cacheBotPackageAssignment(organisation.id, next)
          setPackageId(String(next.packageId))
          setAssignment(next)
        }
      })
      .catch(() => null)
    return () => {
      cancelled = true
    }
  }, [open, organisation?.id])

  useEffect(() => {
    if (!open) return undefined
    let cancelled = false
    fetchPackages()
      .then(list => {
        if (cancelled) return
        const live = (list || []).filter(
          pkg => pkg.status === PACKAGE_STATUS.LIVE && enabledCount(pkg) > 0,
        )
        setHasLivePackages(live.length > 0)
      })
      .catch(() => {
        if (!cancelled) setHasLivePackages(false)
      })
    return () => {
      cancelled = true
    }
  }, [open])

  if (!organisation) return null

  const carePlanEnabled = isBotPackageCarePlanEnabled(assignment)
  const supervisionEnabled = isBotPackageSupervisionEnabled(assignment)
  const visibleLimitMetrics = carePlanEnabled ? LIMIT_METRICS : []
  const { min: limitMin, max: limitMax } = getCachedUsageLimitRange()

  const setMetric = (metric, patch) =>
    setForm(prev => ({ ...prev, [metric]: { ...prev[metric], ...patch } }))

  const step = (metric, delta) => {
    const entry = form[metric]
    // Never step below the minimum or usage already consumed this month, and
    // never above the maximum the backend accepts.
    const floor = Math.max(limitMin, typeof entry.used === 'number' ? entry.used : 0)
    setMetric(metric, {
      value: String(Math.min(limitMax, Math.max(floor, (Number(entry.value) || 0) + delta))),
    })
  }

  const invalid = visibleLimitMetrics.some(metric => {
    const entry = form[metric]
    if (entry.unlimited) return false
    const parsed = Number(entry.value)
    return entry.value === '' || Number.isNaN(parsed) || parsed < limitMin || parsed > limitMax
  })

  // The package is read-only here, so only limits and supervision mode count as edits.
  const limitsChanged = visibleLimitMetrics.some(metric => {
    const current = form[metric]
    const initial = initialForm[metric]
    if (current.unlimited !== initial.unlimited) return true
    return !current.unlimited && Number(current.value) !== Number(initial.value)
  })
  const supervisionChanged = supervisionEnabled && supervisionMode !== initialSupervisionMode
  const hasChanges = limitsChanged || supervisionChanged

  const handleSave = () => {
    const limits = visibleLimitMetrics.reduce((acc, metric) => {
      acc[metric] = form[metric].unlimited ? null : Number(form[metric].value)
      return acc
    }, {})

    const supervisionModeChanged =
      supervisionEnabled &&
      supervisionMode !== (organisation.supervisionMode || SUPERVISION_MODE.DEFAULT)

    setSaving(true)

    const persistAssignment = () => {
      if (!packageId) return Promise.resolve(null)
      const currentId = getOrgPackageId(organisation.id)
      if (currentId && String(currentId) === String(packageId)) {
        return Promise.resolve(null)
      }
      return assignOrChangeBotPackage(organisation.id, packageId).then(next => {
        cacheBotPackageAssignment(organisation.id, next)
        setOrgPackageId(organisation.id, packageId)
        return next
      })
    }

    persistAssignment()
      .then(() => {
        const patch = {}
        // Never send usageLimit when care plan is off — that would re-enable it.
        if (carePlanEnabled) {
          const auditLimit = limits[USAGE_METRIC.CARE_PLAN_AUDITS]
          patch.usageLimit =
            auditLimit == null
              ? { enabled: true, unlimited: true }
              : { enabled: true, unlimited: false, limit: Number(auditLimit) }
        }
        if (supervisionModeChanged) {
          patch.supervision = supervisionModeToConfig(supervisionMode)
        }
        if (Object.keys(patch).length === 0) return null
        return updateBotPackageConfig(organisation.id, patch)
      })
      .then(next => {
        if (next) {
          cacheBotPackageAssignment(organisation.id, next)
          setAssignment(next)
        }
        alertSuccess('Monthly limits updated.', 2000)
        onSaved(
          organisation.id,
          limits,
          supervisionModeChanged ? supervisionMode : undefined,
        )
        onClose()
      })
      .catch(err => {
        alertError(err?.message || 'Could not save monthly limits.')
      })
      .finally(() => setSaving(false))
  }

  return (
    <Modal open={open} onClose={onClose} classes={{ paper: classes.dialogPaper }} tabIndex="-1">
      <div className={classes.body}>
        <div className={classes.header}>
          <h3 className={classes.title}>Monthly limits — {organisation.name}</h3>
          <p className={classes.intro}>
            Allowances reset every month based on the date the bot was created or the date the package
            was last switched. Lowering a limit below what has already been used this month does not
            claw anything back; it stops further use until the reset.
          </p>
        </div>

        {hasLivePackages && (
          <div className={classes.section}>
            <div className={classes.sectionHead}>
              <span className={classes.iconChip}>
                <Glyph paths={ICON.pkg} />
              </span>
              <div className={classes.headText}>
                <h4 className={classes.heading}>Package</h4>
                <p className={classes.desc}>
                  The package this organisation is on. Features below are included with this package
                  and cannot be changed here.
                </p>
              </div>
            </div>

            {/* Read-only view of the assigned package — switching is create-time only. */}
            <TierSelect value={packageId} onChange={setPackageId} hideLabel selectedOnly />
          </div>
        )}

        {visibleLimitMetrics.map(metric => {
          const meta = USAGE_METRIC_META[metric]
          const entry = form[metric]
          const iconPaths = ICON.audits
          const belowUsage =
            !entry.unlimited &&
            entry.used !== null &&
            entry.value !== '' &&
            Number(entry.value) < entry.used
          const outOfRange =
            !entry.unlimited &&
            entry.value !== '' &&
            !Number.isNaN(Number(entry.value)) &&
            (Number(entry.value) < limitMin || Number(entry.value) > limitMax)
          const usageFloor = typeof entry.used === 'number' ? entry.used : null
          const atUsageFloor =
            usageFloor != null &&
            entry.value !== '' &&
            !Number.isNaN(Number(entry.value)) &&
            Number(entry.value) <= Math.max(limitMin, usageFloor)
          const atMinFloor =
            entry.value !== '' &&
            !Number.isNaN(Number(entry.value)) &&
            Number(entry.value) <= limitMin
          const atMaxCeiling =
            entry.value !== '' &&
            !Number.isNaN(Number(entry.value)) &&
            Number(entry.value) >= limitMax
          const decreaseDisabled = entry.unlimited || atUsageFloor || atMinFloor
          const increaseDisabled = entry.unlimited || atMaxCeiling

          // Colour the "used this month" line by how close it is to the limit.
          const limitNum = entry.unlimited ? Infinity : Number(entry.value)
          let usedClass = classes.usedOk
          if (!entry.unlimited && entry.value !== '' && !Number.isNaN(limitNum) && limitNum >= 0) {
            if (entry.used >= limitNum) usedClass = classes.usedSpent
            else if (limitNum > 0 && entry.used / limitNum >= 0.8) usedClass = classes.usedWarn
          }

          return (
            <div className={classes.section} key={metric}>
              <div className={classes.sectionHead}>
                <span className={classes.iconChip}>
                  <Glyph paths={iconPaths} />
                </span>
                <div className={classes.headText}>
                  <h4 className={classes.heading}>{meta.label}</h4>
                  <p className={classes.desc}>{meta.adminHint}</p>
                  {entry.used !== null && (
                    <span className={`${classes.usedNow} ${usedClass}`}>
                      {entry.used} used this month
                    </span>
                  )}
                </div>
              </div>

              <div className={classes.controls}>
                <div
                  className={`${classes.stepper} ${entry.unlimited ? classes.stepperDisabled : ''}`}>
                  <button
                    type="button"
                    className={classes.stepBtn}
                    disabled={decreaseDisabled}
                    aria-label={`Decrease ${meta.label} limit`}
                    onClick={() => step(metric, -1)}>
                    <Glyph paths={ICON.minus} size={18} />
                  </button>
                  <input
                    className={classes.stepInput}
                    type="text"
                    inputMode="numeric"
                    value={entry.unlimited ? '∞' : entry.value}
                    disabled={entry.unlimited}
                    maxLength={String(limitMax).length}
                    aria-label={`${meta.label} monthly limit`}
                    onChange={event =>
                      setMetric(metric, { value: event.target.value.replace(/[^0-9]/g, '') })
                    }
                  />
                  <button
                    type="button"
                    className={classes.stepBtn}
                    disabled={increaseDisabled}
                    aria-label={`Increase ${meta.label} limit`}
                    onClick={() => step(metric, 1)}>
                    <Glyph paths={ICON.plus} size={18} />
                  </button>
                </div>

                <label className={classes.unlimited}>
                  <input
                    type="checkbox"
                    className={classes.hiddenInput}
                    checked={entry.unlimited}
                    onChange={event => setMetric(metric, { unlimited: event.target.checked })}
                  />
                  <span
                    className={`${classes.box} ${entry.unlimited ? classes.boxChecked : ''}`}
                    aria-hidden="true">
                    {entry.unlimited && <Glyph paths={ICON.check} size={12} />}
                  </span>
                  Unlimited
                </label>
              </div>

              {outOfRange && (
                <span className={classes.belowUsage}>
                  Usage limit must be between {limitMin} and {limitMax}.
                </span>
              )}
              {belowUsage && !outOfRange && (
                <span className={classes.belowUsage}>
                  This is below the {entry.used} already used this month — they will have none left
                  until the reset.
                </span>
              )}
            </div>
          )
        })}

        {supervisionEnabled && (
          <div className={classes.section}>
            <div className={classes.sectionHead}>
              <span className={classes.iconChip}>
                <Glyph paths={ICON.sessions} />
              </span>
              <div className={classes.headText}>
                <h4 className={classes.heading}>{USAGE_METRIC_META[USAGE_METRIC.SUPERVISION_SESSIONS].label}</h4>
                <p className={classes.desc}>How this organisation's supervision sessions are managed</p>
              </div>
            </div>

            <div className={classes.controls}>
              <div className={classes.select} ref={supervisionSelectRef}>
                <Select
                  classNamePrefix={'supervision-mode-select'}
                  aria-label="Supervision mode"
                  isSearchable={false}
                  options={SUPERVISION_MODE_SELECT_OPTIONS}
                  value={SUPERVISION_MODE_SELECT_OPTIONS.find(option => option.value === supervisionMode) || null}
                  onChange={option => setSupervisionMode(option ? option.value : SUPERVISION_MODE.DEFAULT)}
                  styles={SUPERVISION_MODE_SELECT_STYLES}
                  menuShouldScrollIntoView={false}
                  onMenuOpen={revealSupervisionMenu}
                  onMenuClose={handleSupervisionMenuClose}
                  closeMenuOnScroll={shouldCloseSupervisionMenuOnScroll}
                />
              </div>
            </div>
          </div>
        )}

        <div className={classes.actions}>
          <button className={classes.ghost} onClick={onClose}>
            Cancel
          </button>
          <button className={classes.primary} disabled={invalid || saving || !hasChanges} onClick={handleSave}>
            {saving ? 'Saving…' : 'Save limits'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

ManageLimitsModal.propTypes = {
  classes: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  organisation: PropTypes.object,
  usage: PropTypes.object,
  onSaved: PropTypes.func,
}

export default withStyles(styles)(ManageLimitsModal)
